import { DAYS, ALL_DAYS, HALF_SLOTS, TIER_ORDER, isInstructionGroup } from "./tiers";
import { getGroupSchedule, parseTime } from "./time";
import { groupsTimeOverlap } from "./validation";
import { countNeedingPlacement, countDeferred, countNoFit } from "./placement";

// A group counts toward scheduled totals only once it has a day, a start
// time, and a real duration — an unscheduled shell has no hours to give.
export function isScheduled(group) {
  const { startTime, duration } = getGroupSchedule(group);
  return Boolean(group.day && startTime && duration);
}

function hoursOf(group) {
  return getGroupSchedule(group).duration || 0;
}

// The largest number of groups running at the same instant on any single
// day. Because one coach can only teach one group at a time, this is the
// floor on how many coaches you need on the court at peak — no schedule can
// be staffed by fewer people than its busiest moment.
function computePeakConcurrency(scheduled) {
  let peak = 0;
  let peakDay = null;
  let peakTime = null;
  DAYS.forEach(day => {
    const dayGroups = scheduled.filter(g => g.day === day);
    HALF_SLOTS.forEach(slot => {
      const mins = parseTime(slot);
      const active = dayGroups.filter(g => {
        const { startTime, duration } = getGroupSchedule(g);
        const start = parseTime(startTime);
        return mins >= start && mins < start + duration * 60;
      }).length;
      if (active > peak) {
        peak = active;
        peakDay = day;
        peakTime = slot;
      }
    });
  });
  return { peak, peakDay, peakTime };
}

export function computeStats(players, groups, coaches = []) {
  const scheduled = groups.filter(isScheduled);
  const unscheduledGroups = groups.filter(g => !isScheduled(g));
  // Weekday group instruction only — weekend open-play/private sessions are
  // excluded from program metrics (hours, spots, fill rate, peak staffing).
  const instruction = scheduled.filter(isInstructionGroup);

  const totalHours = instruction.reduce((sum, g) => sum + hoursOf(g), 0);

  // Hours + group counts broken down by tier.
  const byTier = TIER_ORDER.filter(t => t !== "No Group").map(tier => {
    const tierGroups = groups.filter(g => g.tier === tier);
    const tierScheduled = tierGroups.filter(isScheduled);
    return {
      tier,
      groups: tierGroups.length,
      hours: tierScheduled.reduce((sum, g) => sum + hoursOf(g), 0),
    };
  }).filter(r => r.groups > 0);

  // Hours per day across the full week (includes weekend court activity).
  const byDay = ALL_DAYS.map(day => ({
    day,
    hours: scheduled.filter(g => g.day === day).reduce((sum, g) => sum + hoursOf(g), 0),
    groups: scheduled.filter(g => g.day === day).length,
  }));

  // Hours by court type (Tennis vs Pickleball).
  const byCourtType = ["Tennis", "Pickleball"].map(type => ({
    type,
    hours: scheduled
      .filter(g => (g.courtType || (g.court?.startsWith("Pickleball") ? "Pickleball" : "Tennis")) === type)
      .reduce((sum, g) => sum + hoursOf(g), 0),
  })).filter(r => r.hours > 0);

  const assignedPlayers = players.filter(p => p.groupId).length;
  const totalSpots = instruction.length * 4;
  const filledSpots = instruction.reduce((sum, g) => sum + g.playerIds.length, 0);

  const { peak, peakDay, peakTime } = computePeakConcurrency(instruction);

  // Per-coach load, plus flags for over-capacity and double-booking.
  const coachStats = coaches.map(coach => {
    const coachGroups = scheduled.filter(g => g.coachId === coach.id);
    const hours = coachGroups.reduce((sum, g) => sum + hoursOf(g), 0);
    // Hours this coach teaches each day (weekends included, for privates).
    const hoursByDay = Object.fromEntries(ALL_DAYS.map(day => [
      day,
      coachGroups.filter(g => g.day === day).reduce((sum, g) => sum + hoursOf(g), 0),
    ]));
    // A coach is double-booked if any two of their groups overlap in time.
    let doubleBooked = false;
    for (let i = 0; i < coachGroups.length && !doubleBooked; i++) {
      for (let j = i + 1; j < coachGroups.length; j++) {
        if (groupsTimeOverlap(coachGroups[i], coachGroups[j])) { doubleBooked = true; break; }
      }
    }
    return {
      coach,
      groupCount: coachGroups.length,
      hours,
      hoursByDay,
      overCapacity: coach.maxHours != null && hours > coach.maxHours,
      doubleBooked,
    };
  });

  const assignedCoachHours = coachStats.reduce((sum, c) => sum + c.hours, 0);
  // Open-play sessions never need a coach, so they don't count as "unassigned".
  const unassignedGroups = scheduled.filter(g => !g.coachId && g.kind !== "openplay");
  const unassignedGroupHours = unassignedGroups.reduce((sum, g) => sum + hoursOf(g), 0);

  return {
    totalHours,
    totalGroups: groups.length,
    scheduledCount: scheduled.length,
    unscheduledCount: unscheduledGroups.length,
    byTier,
    byDay,
    byCourtType,
    totalPlayers: players.length,
    assignedPlayers,
    // Excludes members sitting the term out and those no clinic time fits,
    // so this agrees with the header and the Issues list rather than
    // contradicting them.
    unassignedPlayers: countNeedingPlacement(players),
    deferredPlayers: countDeferred(players),
    noFitPlayers: countNoFit(players),
    totalSpots,
    filledSpots,
    fillRate: totalSpots > 0 ? filledSpots / totalSpots : 0,
    peakConcurrency: peak,
    peakDay,
    peakTime,
    coachStats,
    coachCount: coaches.length,
    assignedCoachHours,
    unassignedGroupCount: unassignedGroups.length,
    unassignedGroupHours,
  };
}
